/**
 * Arbre de routes interne du module gestion-eau (monté sous /gestion-eau/* par AppLayout).
 * L'accès global est garanti par <GestionEauRoute> (en amont) ; chaque écran est de plus
 * protégé par <EauRoleProtectedRoute> (rôles cumulables) → matrice d'accès appliquée à 3
 * niveaux (gardes de route + filtrage de nav + scoping des données côté service).
 *
 * Navigation : les BOUTONS-THÈMES vivent dans BottomNav + nav desktop du header.
 *  - Relevés (/releves) regroupe Bassin/Compteur via onglets internes (EauRelevesPage).
 *  - Suivi (/suivi) regroupe Anomalies/Bilans (EauSuiviPage).
 * Les anciennes routes (saisie-bassin, saisie-compteur, anomalies) redirigent vers ces thèmes.
 */
import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import EauRoleProtectedRoute from './EauRoleProtectedRoute';
import { useGestionEau } from '../context/GestionEauContext';
import { accueilElec, domaineDeRoute, EAU_HOME, ELEC_HOME } from '../utils/domaines';
import AppVersionPage from '../../../pages/AppVersionPage';

const EauDashboard = React.lazy(() => import('./EauDashboard'));
const EauConfigPage = React.lazy(() => import('./EauConfigPage'));
const EauRelevesPage = React.lazy(() => import('./EauRelevesPage'));
const EauSuiviPage = React.lazy(() => import('./EauSuiviPage'));
const EauCompteursPage = React.lazy(() => import('./EauCompteursPage'));
const EauFacturationPage = React.lazy(() => import('./EauFacturationPage'));
const EauUtilisateursPage = React.lazy(() => import('./EauUtilisateursPage'));
const EauDemandesPage = React.lazy(() => import('./EauDemandesPage'));
const EauClientPage = React.lazy(() => import('./EauClientPage'));
// Simulation de rôle (Phase 3) — page dédiée. Garde interne sur `realRoles.admin`
// (PAS d'EauRoleProtectedRoute : celui-ci gate sur les rôles EFFECTIFS, ce qui
// rendrait la page injoignable pendant une simulation où admin effectif = false).
const EauSimulationPage = React.lazy(() => import('./EauSimulationPage'));
// Phase 4 — écrans de pilotage (secondaires, accessibles via le menu HeaderEauActions).
const EauTendancesPage = React.lazy(() => import('./EauTendancesPage'));
const EauAlertesPage = React.lazy(() => import('./EauAlertesPage'));
const EauRapportsPage = React.lazy(() => import('./EauRapportsPage'));
const EauAnnoncesPage = React.lazy(() => import('./EauAnnoncesPage'));
const EauAuditPage = React.lazy(() => import('./EauAuditPage'));
// Phase 1 élec — coûts électricité du mois (secondaire, menu HeaderEauActions).
const EauElecCoutsPage = React.lazy(() => import('./EauElecCoutsPage'));
// ELEC-CC-01 : tableau de bord (d'attente) du domaine Électricité.
const EauElecHomePage = React.lazy(() => import('./EauElecHomePage'));
// ELEC-CC-02 : relevés, clients et compteurs, réglages de RTB Énergie.
const EauElecRelevesPage = React.lazy(() => import('./elec/EauElecRelevesPage'));
const EauElecClientsPage = React.lazy(() => import('./elec/EauElecClientsPage'));
const EauElecReglagesPage = React.lazy(() => import('./elec/EauElecReglagesPage'));
const EauElecFacturesPage = React.lazy(() => import('./elec/EauElecFacturesPage'));

const Loader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ahuvi-forest"></div>
  </div>
);

/**
 * Garde de DOMAINE (ELEC-CC-01). Démarrage à froid : aucune redirection tant que la
 * session et les rôles ne sont pas confirmés (§2). Ensuite :
 *  - route eau, personne sans eau mais avec l'électricité → tableau de bord électricité ;
 *  - route électricité, personne sans électricité mais avec l'eau → tableau de bord eau ;
 *  - entrée du module (/gestion-eau) avec l'électricité mémorisée → électricité ;
 *  - toute autre route d'un domaine de la personne devient le domaine actif, mais
 *    SEULEMENT quand l'adresse change : le sélecteur change d'abord le domaine puis
 *    l'adresse (rendu intermédiaire « ancienne adresse + nouveau domaine »), qu'il ne
 *    faut pas annuler.
 */
function EauDomaineGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { roles, domaines, domaineActif, setDomaineActif, isLoading, rolesConfirmed, sessionStatus } = useGestionEau();
  const ready = !isLoading && rolesConfirmed && sessionStatus === 'valid';
  const routeDom = domaineDeRoute(pathname);
  const isEntree = pathname.replace(/\/+$/, '') === EAU_HOME;

  let redirect: string | null = null;
  if (ready && routeDom === 'eau' && !domaines.eau && domaines.elec) redirect = accueilElec(roles);
  else if (ready && routeDom === 'elec' && !domaines.elec && domaines.eau) redirect = EAU_HOME;
  else if (ready && isEntree && domaines.elec && domaineActif === 'elec') redirect = accueilElec(roles);

  const domaineActifRef = useRef(domaineActif);
  domaineActifRef.current = domaineActif;
  const canSync = ready && !redirect && !!routeDom && domaines[routeDom];
  useEffect(() => {
    if (canSync && routeDom && routeDom !== domaineActifRef.current) setDomaineActif(routeDom);
    // Déclenché par un changement d'adresse (ou la confirmation des rôles), jamais par
    // un changement de domaine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, canSync]);

  if (redirect) return <Navigate to={redirect} replace />;
  return <>{children}</>;
}

export default function GestionEauRoutes() {
  return (
    <Suspense fallback={<Loader />}>
      {/* Marge basse scopée au module : la BottomNav du module (libellés sur 2 lignes
          « Tableau de bord » / « Facturation » + zone sûre Android) dépasse les 80px
          du <main>, recouvrant la dernière carte. Ce padding additif laisse un espace
          net au-dessus de la barre, sans impacter les autres modules. */}
      <div className="pb-[calc(5rem+env(safe-area-inset-bottom))]">
      <EauDomaineGate>
      <Routes>
        {/* Tableau de bord opérationnel — admin/releveur (le client est redirigé vers son espace). */}
        <Route
          index
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'releveur', 'promoteur']}>
              <EauDashboard />
            </EauRoleProtectedRoute>
          }
        />

        {/* Thème Relevés (Bassin · Compteur · Tournée · Scan) */}
        <Route
          path="releves"
          element={
            <EauRoleProtectedRoute allowedRoles={['releveur', 'admin', 'promoteur']}>
              <EauRelevesPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Thème Suivi (Anomalies/Bilans · Tendances) */}
        <Route
          path="suivi"
          element={
            <EauRoleProtectedRoute allowedRoles={['releveur', 'admin', 'promoteur']}>
              <EauSuiviPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Thème Compteurs (Liste CRUD + QR · Carte) — admin */}
        <Route
          path="compteurs"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauCompteursPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Thème Facturation (Factures · Rapports) — admin */}
        <Route
          path="facturation"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauFacturationPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Phase 4 — Tendances (lecture) : admin + releveur. */}
        <Route
          path="tendances"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'releveur', 'promoteur']}>
              <EauTendancesPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Phase 4 — Alertes / Rapports / Annonces / Audit : admin uniquement. */}
        <Route
          path="alertes"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauAlertesPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="rapports"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauRapportsPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="annonces"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauAnnoncesPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="audit"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauAuditPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Écrans secondaires (menu en haut à droite) — admin */}
        <Route
          path="config"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauConfigPage />
            </EauRoleProtectedRoute>
          }
        />
        {/* Phase 1 élec : coûts électricité du mois (prix variable, abandonné par ELEC-CC-01) :
            hors des menus, gardé pour l'historique, réservé à l'admin. */}
        <Route
          path="elec-couts"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin']}>
              <EauElecCoutsPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* ELEC-CC-01 : domaine Électricité, tableau de bord d'attente. */}
        <Route
          path="elec"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur', 'gestionElec', 'lectureElec', 'client']}>
              <EauElecHomePage />
            </EauRoleProtectedRoute>
          }
        />
        {/* ELEC-CC-02 : relevés (seul écran du releveur électricité), clients et compteurs, réglages. */}
        <Route
          path="elec/releves"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur', 'releveurElec', 'gestionElec', 'lectureElec']}>
              <EauElecRelevesPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="elec/clients"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur', 'gestionElec', 'lectureElec']}>
              <EauElecClientsPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="elec/reglages"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur', 'gestionElec', 'lectureElec']}>
              <EauElecReglagesPage />
            </EauRoleProtectedRoute>
          }
        />
        {/* ELEC-CC-03 : factures RTB (gestion : émission ; valideurs : avis ; lecture et promoteur : consultation). */}
        <Route
          path="elec/factures"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur', 'gestionElec', 'lectureElec', 'validationElec']}>
              <EauElecFacturesPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route path="elec/*" element={<Navigate to={ELEC_HOME} replace />} />
        <Route
          path="utilisateurs"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauUtilisateursPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="demandes"
          element={
            <EauRoleProtectedRoute allowedRoles={['admin', 'promoteur']}>
              <EauDemandesPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Espace client (Ma conso / Mes factures via :tab) — client/admin (supervision) */}
        <Route
          path="client"
          element={
            <EauRoleProtectedRoute allowedRoles={['client', 'admin']}>
              <EauClientPage />
            </EauRoleProtectedRoute>
          }
        />
        <Route
          path="client/:tab"
          element={
            <EauRoleProtectedRoute allowedRoles={['client', 'admin']}>
              <EauClientPage />
            </EauRoleProtectedRoute>
          }
        />

        {/* Simulation de rôle (Phase 3) — admin RÉEL uniquement (garde interne à la page).
            Atteignable via la ligne du menu HeaderEauActions ou le deep-link ; NON exposée
            dans BottomNav/nav. Reste accessible PENDANT une simulation (garde sur realRoles). */}
        <Route path="simulation" element={<EauSimulationPage />} />

        {/* Compatibilité : anciennes routes Phase 1-2 → thèmes correspondants */}
        <Route path="saisie-bassin" element={<Navigate to="/gestion-eau/releves" replace />} />
        <Route path="saisie-compteur" element={<Navigate to="/gestion-eau/releves" replace />} />
        <Route path="anomalies" element={<Navigate to="/gestion-eau/suivi" replace />} />

        {/* Page version/mise à jour, montée sous le préfixe du module pour ne pas sortir
            du module Eau (sinon le switcher rebascule sur BazarKELY). Accessible à tous
            les rôles du module — pas de garde de rôle. */}
        <Route path="version" element={<AppVersionPage />} />

        <Route path="*" element={<Navigate to="/gestion-eau" replace />} />
      </Routes>
      </EauDomaineGate>
      </div>
    </Suspense>
  );
}
